import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import type { Translation, TranslocoLoader } from '@jsverse/transloco';
import { catchError, forkJoin, map, Observable, shareReplay, throwError } from 'rxjs';

import { GLOBAL_CATALOG_DOMAINS } from './i18n.config';

/**
 * Loads catalogs from `public/i18n/<locale>/<domain>.json`. A bare language (`es`) loads and merges
 * every global domain under its own namespace (`common`, `errors`, `layout`); a scope path
 * (`admin/tool-management/es`) loads that one feature file.
 */
@Injectable({ providedIn: 'root' })
export class FuryTranslocoLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);
  private readonly requests = new Map<string, Observable<Translation>>();

  getTranslation(path: string): Observable<Translation> {
    const slash = path.lastIndexOf('/');
    if (slash === -1) {
      return forkJoin(GLOBAL_CATALOG_DOMAINS.map((domain) => this.fetch(path, domain))).pipe(
        map((parts) =>
          Object.fromEntries(GLOBAL_CATALOG_DOMAINS.map((domain, i) => [domain, parts[i]])),
        ),
      );
    }
    return this.fetch(path.slice(slash + 1), path.slice(0, slash));
  }

  /**
   * One request per catalog file, shared by every caller. Transloco loads a scoped catalog twice
   * when the active language is also the fallback one, so the duplicate is absorbed here. A failed
   * file is forgotten, so selecting the language again retries it.
   */
  private fetch(lang: string, domain: string): Observable<Translation> {
    const url = `i18n/${lang}/${domain}.json`;
    let request = this.requests.get(url);
    if (!request) {
      request = this.http.get<Translation>(url).pipe(
        catchError((error: unknown) => {
          this.requests.delete(url);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.requests.set(url, request);
    }
    return request;
  }
}
